import type { Transaction } from '@tanstack/vue-db'
import { createOptimisticAction } from '@tanstack/vue-db'
import { z } from 'zod'
import { useRhythmicLogger } from '#imports'

import { getWorkitemTagsCollection } from '../../getCollections'

const logger = useRhythmicLogger('useTeamContext:deleteWorkitemTag')

export interface DeleteWorkitemTagPayload {
  workitemId: string
  eid: string
}

export interface WorkitemTagMutationContext {
  teamId: string
}

const DeleteWorkitemTagSchema = z.object({
  workitemId: z.string().min(1),
  eid: z.string().uuid(),
})

function validateParams(payload: DeleteWorkitemTagPayload, context: WorkitemTagMutationContext) {
  if (!context.teamId) throw new Error('Team context is not ready')

  const parsed = DeleteWorkitemTagSchema.parse(payload)

  return {
    ...parsed,
    teamId: context.teamId,
  }
}

export default function handleDeleteWorkitemTag(
  payload: DeleteWorkitemTagPayload,
  context: WorkitemTagMutationContext,
): Transaction {
  const resolved = validateParams(payload, context)
  const workitemTagsCollection = getWorkitemTagsCollection(resolved.teamId)

  const action = createOptimisticAction<{
    payload: DeleteWorkitemTagPayload
    context: WorkitemTagMutationContext
  }>({
    onMutate: () => {
      logger.debug('Optimistically deleting workitem tag', {
        eid: resolved.eid,
      })
      workitemTagsCollection.delete(resolved.eid)
    },
    mutationFn: async () => {
      const result = await $fetch<{ txid?: string | number }>(`/api/workitems/${resolved.workitemId}/tags` as string, {
        method: 'DELETE',
        body: {
          eid: resolved.eid,
        },
      })

      if (result?.txid && workitemTagsCollection.utils?.awaitTxId)
        await workitemTagsCollection.utils.awaitTxId(Number.parseInt(result.txid.toString()))
    },
  })

  return action({ payload, context })
}
